/**
 * Report serializer utilities for markdown blocks and legacy XML migration.
 */

import { randomUUID } from "@/lib/randomUUID";

export type BlockType = "heading" | "paragraph" | "list" | "codeBlock" | "blockquote";

export interface Block {
  id: string;
  type: BlockType;
  content: string;
  level?: number;
}

export interface ParsedReport {
  title: string;
  metadata: Record<string, string>;
  blocks: Block[];
}

const NEWLINE = "\n";

function generateBlockId(): string {
  return randomUUID();
}

function unescapeXml(text: string): string {
  let result = text;
  let prev = "";

  while (result != prev) {
    prev = result;
    result = result
      .replace(/&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&gt;/g, ">")
      .replace(/&lt;/g, "<")
      .replace(/&amp;/g, "&");
  }

  return result;
}

function blockToMarkdown(block: Block): string {
  switch (block.type) {
    case "heading": {
      const hashes = "#".repeat(block.level ?? 2);
      return `${hashes} ${block.content}`;
    }
    case "paragraph":
      return block.content;
    case "list":
      return block.content;
    case "codeBlock": {
      const matches = block.content.match(/`+/g) || [];
      const longest = matches.reduce((max, seq) => Math.max(max, seq.length), 0);
      const fenceLength = Math.max(3, longest + 1);
      const fence = "`".repeat(fenceLength);
      return `${fence}\n${block.content}\n${fence}`;
    }
    case "blockquote":
      return block.content
        .split(NEWLINE)
        .map((line) => `> ${line}`)
        .join(NEWLINE);
    default:
      return block.content;
  }
}

export function blocksToMarkdown(blocks: Block[]): string {
  return blocks.map((block) => blockToMarkdown(block)).join(NEWLINE + NEWLINE);
}

export function markdownToBlocks(markdown: string): Block[] {
  const blocks: Block[] = [];
  const lines = markdown.split(/\r?\n/);
  let i = 0;
  const fencePattern = /^\s*([`~]{3,})(.*)$/;
  const isFenceLine = (value: string) => fencePattern.test(value);

  while (i < lines.length) {
    const line = lines[i];

    // Skip empty lines
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Check for heading
    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      blocks.push({
        id: generateBlockId(),
        type: "heading",
        content: headingMatch[2].trim(),
        level: headingMatch[1].length,
      });
      i++;
      continue;
    }

    // Check for code block
    const fenceMatch = line.match(fencePattern);
    if (fenceMatch) {
      const fence = fenceMatch[1];
      const codeLines: string[] = [];
      i++;
      const fenceRegex = new RegExp(`^\\s*${fence}[\\s]*$`);
      while (i < lines.length && !fenceRegex.test(lines[i])) {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length) {
        i++; // Skip closing fence
      }
      blocks.push({
        id: generateBlockId(),
        type: "codeBlock",
        content: codeLines.join(NEWLINE),
      });
      continue;
    }

    // Check for blockquote
    if (/^\s*>/.test(line)) {
      const quoteLines: string[] = [];
      while (i < lines.length && (/^\s*>/.test(lines[i]) || lines[i].trim() === "")) {
        if (lines[i].trim() === "") {
          if (i + 1 < lines.length && /^\s*>/.test(lines[i + 1])) {
            quoteLines.push("");
            i++;
          } else {
            break;
          }
        } else {
          quoteLines.push(lines[i].replace(/^\s*>+\s?/, ""));
          i++;
        }
      }
      blocks.push({
        id: generateBlockId(),
        type: "blockquote",
        content: quoteLines.join(NEWLINE),
      });
      continue;
    }

    // Check for list
    if (line.match(/^[\s]*[-*]\s/) || line.match(/^[\s]*\d+\.\s/)) {
      const listLines: string[] = [];
      while (i < lines.length) {
        const currentLine = lines[i];
        if (currentLine.trim() === "") {
          if (
            i + 1 < lines.length &&
            (lines[i + 1].match(/^[\s]*[-*]\s/) || lines[i + 1].match(/^[\s]*\d+\.\s/))
          ) {
            listLines.push("");
            i++;
          } else {
            break;
          }
        } else if (
          currentLine.match(/^[\s]*[-*]\s/) ||
          currentLine.match(/^[\s]*\d+\.\s/) ||
          currentLine.match(/^\s{2,}/)
        ) {
          listLines.push(currentLine);
          i++;
        } else {
          break;
        }
      }
      blocks.push({
        id: generateBlockId(),
        type: "list",
        content: listLines.join(NEWLINE),
      });
      continue;
    }

    // Default: paragraph
    const paragraphLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !lines[i].match(/^#{1,6}\s/) &&
      !isFenceLine(lines[i]) &&
      !/^\s*>/.test(lines[i]) &&
      !lines[i].match(/^[\s]*[-*]\s/) &&
      !lines[i].match(/^[\s]*\d+\.\s/)
    ) {
      paragraphLines.push(lines[i]);
      i++;
    }

    if (paragraphLines.length > 0) {
      blocks.push({
        id: generateBlockId(),
        type: "paragraph",
        content: paragraphLines.join(NEWLINE),
      });
    } else {
      i++;
    }
  }

  return blocks;
}

export function normalizeMarkdown(text: string): string {
  const lines = text.split(NEWLINE);

  return lines
    .map((line) => {
      const listMatch = line.match(/^(\s*)([*-])\s+(.*)$/);
      if (listMatch) {
        return `* ${listMatch[3]}`;
      }

      const numberedMatch = line.match(/^(\s*)(\d+\.)\s+(.*)$/);
      if (numberedMatch) {
        return `${numberedMatch[2]} ${numberedMatch[3]}`;
      }

      return line.trimStart();
    })
    .join(NEWLINE);
}

export function parseXmlToBlocks(xml: string): ParsedReport {
  const result: ParsedReport = {
    title: "",
    metadata: {},
    blocks: [],
  };

  const titleMatch = /<main_title>([\s\S]*?)<\/main_title>/i.exec(xml);
  if (titleMatch) {
    result.title = unescapeXml(titleMatch[1].trim());
  }

  const metaBlock = /<metadata>([\s\S]*?)<\/metadata>/i.exec(xml);
  if (metaBlock) {
    const metaContent = metaBlock[1];
    const dateMatch = /<date>([\s\S]*?)<\/date>/i.exec(metaContent);
    const authorMatch = /<author>([\s\S]*?)<\/author>/i.exec(metaContent);
    if (dateMatch) result.metadata.Date = unescapeXml(dateMatch[1].trim());
    if (authorMatch) result.metadata.Author = unescapeXml(authorMatch[1].trim());
  }

  const sectionRegex = /<section(?:\s+id="([^"]*)")?(?:\s+level="([^"]*)")?\s*>([\s\S]*?)<\/section>/gi;
  let match;

  while ((match = sectionRegex.exec(xml)) !== null) {
    const sectionId = match[1] || generateBlockId();
    const sectionLevel = match[2] ? parseInt(match[2], 10) : 2;
    const sectionContent = match[3];

    const headingMatch = /<heading>([\s\S]*?)<\/heading>/i.exec(sectionContent);
    if (headingMatch) {
      const headingText = unescapeXml(headingMatch[1].trim());
      result.blocks.push({
        id: sectionId,
        type: "heading",
        content: headingText,
        level: sectionLevel,
      });
    }

    const bodyMatch = /<body>([\s\S]*?)<\/body>/i.exec(sectionContent);
    if (bodyMatch) {
      const bodyContent = unescapeXml(bodyMatch[1].trim());
      const normalizedBody = normalizeMarkdown(bodyContent);
      const bodyBlocks = markdownToBlocks(normalizedBody);
      for (const block of bodyBlocks) {
        result.blocks.push(block);
      }
    }
  }

  return result;
}
